import { AnyAction } from "redux";
import { StoreError } from "../../errors";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";
import {
  ACTION_EVENT_RESULTS_CALCULATED,
  ACTION_EVENT_RESULTS_CALCULATE_ERROR,
  ACTION_EVENT_RESULTS_CALCULATING,
} from "./calculate";
import { ACTION_RESULTS_LISTEN_CHANGE } from "./listen-change";

export interface RoundResult {
  index: number;
  selectedBeer: string;
  correctBeer: string;
  correct: boolean;
  tasteScore: number;
  asterisked: boolean;
  points: number;
  changesMade: number;
}

export interface ResultSummary {
  userEmail: string;
  totalPoints: number;
  totalTaste: number;
  totalCorrectAsterisks: number;
  totalCorrectSecondHalf: number;
  totalChanges: number;
  roundResults: RoundResult[];
  beerScores: { [id: string]: number };
  isTied: boolean;
  tieBreakerReason: string | null;
}

export interface BeerRanking {
  name: string;
  points: number;
}

export type Result = {
  id: string;
  roundResults: ResultSummary[];
  beerScoreResults: BeerRanking[];
  lastUpdated: Date | undefined | null;
};

/**
 * Per-event results stored under id-resource (`itemsById[eventId]`). Calculate
 * sub-state is per-event too. Legacy fields (`items`, `itemsLoading`,
 * `itemsError`) are kept as derived mirrors so existing screens continue to
 * work without being touched in this PR.
 */
export type ResultsState = IdResourceState<Result> & {
  // Legacy mirrors:
  items: Result[] | null;
  itemsLoading: { [key: string]: boolean };
  itemsError: { [key: string]: StoreError };
  // Calculate flow (per-event):
  itemsCalculating: { [key: string]: boolean };
  itemsCalculationError: { [key: string]: StoreError };
};

export const RESULTS_ACTION_TYPES = buildIdResourceActionTypes("results");
export const resultsActions = buildIdResourceActions<Result>(
  RESULTS_ACTION_TYPES
);
export const resultsSelectors = buildIdResourceSelectors<
  { results: ResultsState },
  Result
>((root) => root.results);

const idResourceReducer = buildIdResourceReducer<Result, ResultsState>(
  RESULTS_ACTION_TYPES
);

const initialState: ResultsState = {
  ...initialIdResourceState<Result>(),
  items: null,
  itemsLoading: {},
  itemsError: {},
  itemsCalculating: {},
  itemsCalculationError: {},
};

const itemsArrayFromMap = (
  itemsById: Record<string, Result> | undefined
): Result[] =>
  Object.values(itemsById ?? {}).filter((i): i is Result => Boolean(i));

const ITEMSBYID_TOUCHING_ACTIONS = new Set<string>([
  RESULTS_ACTION_TYPES.startedLoading,
  RESULTS_ACTION_TYPES.loaded,
  RESULTS_ACTION_TYPES.errored,
  RESULTS_ACTION_TYPES.removed,
  ACTION_RESULTS_LISTEN_CHANGE,
  ACTION_EVENT_RESULTS_CALCULATED,
]);

/** Mirror id-resource state to legacy fields. See events/reducer.ts for the
 *  same nuance: `items` stays null until something is committed once, then
 *  sticks as an array so post-commit empty states work correctly. The mirror
 *  is only invoked for actions that actually touched itemsById. */
const reflectLegacyMirrors = (state: ResultsState): ResultsState => {
  let items: Result[] | null;
  if (!state.itemsById) {
    items = state.items;
  } else {
    const hasAnyItem = Object.keys(state.itemsById).length > 0;
    const previouslyCommitted =
      state.items !== null && state.items !== undefined;
    items =
      hasAnyItem || previouslyCommitted ? itemsArrayFromMap(state.itemsById) : null;
  }
  return {
    ...state,
    items,
    itemsLoading: state.loadingFor ?? state.itemsLoading ?? {},
    itemsError: state.errorFor
      ? (Object.fromEntries(
          Object.entries(state.errorFor).filter(([, e]) => e !== null)
        ) as { [key: string]: StoreError })
      : state.itemsError ?? {},
  };
};

const commit = (state: ResultsState, item: Result): ResultsState =>
  item?.id
    ? { ...state, itemsById: { ...state.itemsById, [item.id]: item } }
    : state;

export default function resultsReducer(
  state: ResultsState = initialState,
  action: AnyAction
): ResultsState {
  let next = idResourceReducer(state, action);

  switch (action.type) {
    case ACTION_RESULTS_LISTEN_CHANGE:
    case ACTION_EVENT_RESULTS_CALCULATED: {
      const item = action.payload as Result;
      next = commit(next, item);
      if (action.type === ACTION_EVENT_RESULTS_CALCULATED && item?.id) {
        const itemsCalculationError = { ...next.itemsCalculationError };
        delete itemsCalculationError[item.id];
        next = {
          ...next,
          itemsCalculationError,
          requestToken: { ...next.requestToken, [item.id]: (next.requestToken[item.id] ?? 0) + 1 },
          loadingFor: { ...next.loadingFor, [item.id]: false },
          errorFor: { ...next.errorFor, [item.id]: null },
        };
      }
      break;
    }
    case ACTION_EVENT_RESULTS_CALCULATING: {
      const { id, calculating } = action.payload as {
        id: string;
        calculating: boolean;
      };
      next = {
        ...next,
        itemsCalculating: { ...next.itemsCalculating, [id]: calculating },
      };
      break;
    }
    case ACTION_EVENT_RESULTS_CALCULATE_ERROR: {
      const { id, error } = action.payload as {
        id: string;
        error: StoreError;
      };
      next = {
        ...next,
        itemsCalculationError: {
          ...next.itemsCalculationError,
          [id]: error,
        },
      };
      break;
    }
    default:
      break;
  }

  if (ITEMSBYID_TOUCHING_ACTIONS.has(action.type)) {
    return reflectLegacyMirrors(next);
  }
  return next;
}
